import React, {useState, useEffect} from 'react';
import cx from 'classnames';
import _ from 'lodash';

import Link from '../../components/Link';
import FadeOut from '../../components/FadeOut';
import DynamicHeroIcon from '../../components/DynamicHeroIcons';

/**
 * @typedef {Object} Collection
 * @property {string} title The title of the collection e.g. 'Top SQL questions'
 * @property {string} link The link to the collection e.g. '/questions?sortBy=most-popular&type=sql'
 * @property {string} [icon] The icon for the collection (optional) e.g. 'DatabaseIcon'
 * @property {string} [roleCategory] Follows 'role-category' tag, sorts the collections accordingly
 */

/**
 * Collection object
 * @type {Collection[]}
 */
const collections = [
  {
    title: 'Behavioral questions answered by experts',
    link: '/questions?filterBy=expert&sortBy=hot&type=behavioral',
    icon: 'UserGroupIcon',
  },
  {
    title: 'Trending Google PM questions',
    link: '/questions?company=google&role=pm&sortBy=hot',
    icon: 'ChartSquareBarIcon',
    roleCategory: 'product',
  },
  {
    title: 'Recent Meta PM questions',
    link: '/questions?company=facebook&role=pm&sortBy=newest',
    icon: 'ClipboardListIcon',
    roleCategory: 'product',
  },
  {
    title: 'Top product design questions',
    link: '/questions?sortBy=most-popular&type=product-design',
    icon: 'PencilIcon',
    roleCategory: 'product',
  },
  {
    title: 'Watch experts tackle top data science questions',
    link: '/questions?filterBy=video&role=data-science&sortBy=most-popular',
    icon: 'DatabaseIcon',
    roleCategory: 'data',
  },
  {
    title: 'Popular machine learning questions',
    link: '/questions?sortBy=most-popular&type=machine-learning',
    icon: 'LightBulbIcon',
    roleCategory: 'engineering',
  },
  {
    title: 'Data engineering questions',
    link: '/questions?role=data-engineer',
    icon: 'DatabaseIcon',
    roleCategory: 'engineering',
  },
  {
    title: 'Latest questions at Nvidia',
    link: '/questions?company=nvidia&sortBy=newest',
    icon: 'EyeIcon',
  },
  {
    title: 'Top SQL questions',
    link: '/questions?sortBy=most-popular&type=sql',
    icon: 'DatabaseIcon',
  },
  {
    title: 'Top coding questions to practice',
    link: '/questions?filterBy=codeEditor&sortBy=most-popular',
    icon: 'CodeIcon',
    roleCategory: 'engineering',
  },
  {
    title: 'Latest Amazon Solution Architect questions',
    link: '/questions?company=amazon&role=solutions-architect&sortBy=newest',
    icon: 'CloudIcon',
    roleCategory: 'engineering',
  },
];

const Collections = ({path, currentUser, trackerEvent}) => {
  const [finalCollections, setFinalCollections] = useState([]);

  // Function to rotate an array by a given number of positions
  const rotateArray = (array, positions) => {
    if (array.length === 0) return array;
    const rotation = positions % array.length;
    return array.slice(rotation).concat(array.slice(0, rotation));
  };

  // Function to get a consistent color based on the day and index
  const getColor = (day, index) => {
    const colors = [
      {bg: 'bg-indigo-100', text: 'text-indigo-700'},
      {bg: 'bg-green-100', text: 'text-green-700'},
      {bg: 'bg-blue-100', text: 'text-blue-700'},
      {bg: 'bg-yellow-100', text: 'text-yellow-700'},
    ];
    const hash = (index + day) % colors.length;
    return colors[hash];
  };

  // Get role category from currentUser and partition collections once
  useEffect(() => {
    const roleCategory = currentUser?.profile?.career_tracks?.[1] || null;

    const date = new Date();
    const day = date.getDate();

    const partitioned = collections.reduce(
      (acc, collection, index) => {
        const {bg: bgColorClass, text: iconColorClass} = getColor(day, index);

        const formattedCollection = {
          ...collection,
          bgColor: bgColorClass,
          iconColor: iconColorClass,
        };

        if (collection.roleCategory === roleCategory) {
          acc.match.push(formattedCollection);
        } else if (!collection.roleCategory) {
          acc.universal.push(formattedCollection);
        } else {
          acc.nonMatch.push(formattedCollection);
        }

        return acc;
      },
      {match: [], universal: [], nonMatch: []},
    );

    // Rotate each partition individually
    const rotatedMatch = rotateArray(partitioned.match, day);
    const rotatedUniversal = rotateArray(partitioned.universal, day);
    const rotatedNonMatch = rotateArray(partitioned.nonMatch, day);

    const arrangedCollections = [
      ...rotatedMatch,
      ...rotatedUniversal,
      ...rotatedNonMatch,
    ].filter((c) => c.link !== path);

    setFinalCollections(arrangedCollections);
  }, [currentUser, path]);

  if (!collections) return null;

  return (
    <div>
      <FadeOut className="-ml-2 -my-1" right left>
        <div className="w-full py-1 overflow-x-scroll scrollbar-hidden">
          <div className="flex">
            {finalCollections.map((collection) => (
              <Link
                key={collection.title}
                href={collection.link}
                onClick={() => trackerEvent?.({title: collection.title})}
                className="flex-none w-72 mx-2 border border-gray-200 rounded-xl p-3 hover:shadow-sm hover:border-gray-300 transition duration-25"
              >
                <div className="flex items-center">
                  {collection.icon && (
                    <div className={cx('h-12 w-12 flex items-center justify-center', collection.bgColor)}>
                      <DynamicHeroIcon name={collection.icon} className={cx('h-6 w-6', collection.iconColor)} />
                    </div>
                  )}
                  <div className={cx(
                    {'ml-2': collection.icon, 'flex-grow justify-center': !collection.icon},
                    'flex items-center h-12 w-56 font-semibold leading-tight',
                  )}
                  >
                    {_.truncate(collection.title, {length: 50})}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </FadeOut>
    </div>
  );
};

export default Collections;
